import React, { useState } from 'react';
import { Button, Divider, ListItemText, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import { AutoAwesomeOutlined as AutoAwesomeOutlinedIcon, Check as CheckIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';

// Orden del menú: de mayor a menor capacidad, y dentro de cada familia la
// versión más reciente primero.
const FAMILY_ORDER = { fable: 0, opus: 1, sonnet: 2, haiku: 3 };

export const sortModelsForMenu = (models) => [...models].sort((a, b) => (
  (FAMILY_ORDER[a.family] ?? 9) - (FAMILY_ORDER[b.family] ?? 9)
  || (b.versionNumber || 0) - (a.versionNumber || 0)
  || String(a.name).localeCompare(String(b.name))
));

/**
 * Selector de modelo del chat (junto a los modos de análisis).
 * `models`: modelos configurados ya descritos (utils/llmModels.describeModel),
 * con `availableInPlan === false` si el plan de la cuenta no lo incluye.
 * `value`: ID del modelo elegido, o null = "Modelo del agente".
 */
const ChatModelSelector = ({ models, value, onChange, disabled = false }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const selected = value ? models.find((m) => m.id === value) : null;
  const label = selected ? selected.name : 'Modelo del agente';

  const choose = (modelId) => {
    setAnchorEl(null);
    onChange(modelId);
  };

  const itemText = (primary, secondary) => (
    <ListItemText
      primary={primary}
      secondary={secondary}
      primaryTypographyProps={{ sx: { color: 'var(--wb-text)', fontSize: '0.9rem' } }}
      secondaryTypographyProps={{ sx: { color: 'var(--wb-text-soft)', fontSize: '0.75rem' } }}
    />
  );

  return (
    <>
      <Button
        startIcon={<AutoAwesomeOutlinedIcon />}
        endIcon={<ExpandMoreIcon />}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        disabled={disabled}
        aria-haspopup="menu"
        aria-label={`Modelo: ${label}`}
        sx={{
          color: 'var(--wb-x-text-cccccc, #ccc)',
          '&:hover': { backgroundColor: 'var(--wb-x-bg-160565b7, #160565b7)' },
          '&.Mui-disabled': { color: 'var(--wb-x-text-cccccc66, rgba(204,204,204,0.4))' },
          borderRadius: 2,
          textTransform: 'none',
          px: 1.5,
          maxWidth: '100%',
          '& .MuiButton-startIcon, & .MuiButton-endIcon': { flexShrink: 0 }
        }}
        type="button"
      >
        <Typography component="span" noWrap sx={{ fontSize: 'inherit' }}>{label}</Typography>
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{
          sx: {
            backgroundColor: 'var(--wb-bg-surface-alt)',
            color: 'var(--wb-text)',
            minWidth: 260,
            maxHeight: 420,
            '& .MuiMenuItem-root:hover': { backgroundColor: 'var(--wb-accent-tint)' }
          }
        }}
      >
        <MenuItem selected={!value} onClick={() => choose(null)}>
          {itemText('Modelo del agente', 'El que tiene configurado el agente del espacio de trabajo')}
          {!value && <CheckIcon fontSize="small" sx={{ ml: 1, color: 'var(--wb-accent)' }} />}
        </MenuItem>
        <Divider sx={{ borderColor: 'var(--wb-border)' }} />
        {sortModelsForMenu(models).map((model) => {
          const locked = model.availableInPlan === false;
          const item = (
            <MenuItem key={model.id} selected={model.id === value} disabled={locked} onClick={() => choose(model.id)}>
              {itemText(model.name, locked ? 'Disponible en planes de pago' : `Rendimiento ${String(model.performance).toLowerCase()} · velocidad ${String(model.speed).toLowerCase()}`)}
              {model.id === value && <CheckIcon fontSize="small" sx={{ ml: 1, color: 'var(--wb-accent)' }} />}
            </MenuItem>
          );
          return locked ? (
            <Tooltip key={model.id} title="Se habilita en un plan de pago" placement="right">
              <span>{item}</span>
            </Tooltip>
          ) : item;
        })}
      </Menu>
    </>
  );
};

export default ChatModelSelector;
